// frontend/src/contexts/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react'
import toast from 'react-hot-toast'

export const AuthContext = createContext({})

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [token, setToken] = useState(localStorage.getItem('token'))
  const [loading, setLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      const savedToken = localStorage.getItem('token')
      const savedUser = localStorage.getItem('user')
      
      if (savedToken && savedUser) {
        try {
          const userData = JSON.parse(savedUser)
          setToken(savedToken)
          setUser(userData)
          setIsAuthenticated(true)
          
          // Try to verify token, but don't immediately logout on failure
          try {
            await verifyToken(savedToken)
          } catch (verifyError) {
            console.warn('Token verification failed, but keeping user logged in:', verifyError)
            // Only logout if the token is explicitly invalid
            if (verifyError.message === 'Token verification failed' && verifyError.status === 401) {
              logout()
            }
          }
        } catch (error) {
          console.error('Error parsing saved user data:', error)
          // Only logout if we can't parse the user data
          if (error instanceof SyntaxError) {
            logout()
          }
        }
      }
      setLoading(false)
    }

    checkAuth()
  }, [])

  const verifyToken = async (tokenToVerify) => {
    let retryCount = 0
    const maxRetries = 3
    const retryDelay = 1000 // 1 second

    while (retryCount < maxRetries) {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL || 'https://api.testcrew.ai'}/api/auth/me`, {
          headers: {
            'Authorization': `Bearer ${tokenToVerify}`,
            'Content-Type': 'application/json'
          }
        })

        if (!response.ok) {
          if (response.status === 401) {
            throw { message: 'Token verification failed', status: 401 }
          }
          throw new Error('Token verification failed')
        }

        const userData = await response.json()
        setUser(userData)
        return true
      } catch (error) {
        retryCount++
        if (retryCount === maxRetries) {
          throw error
        }
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, retryDelay * retryCount))
      }
    }
  }

  const login = async (credentials) => {
    try {
      setLoading(true)
      
      const response = await fetch(`${import.meta.env.VITE_API_URL || '  https://api.testcrew.ai'}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({
          username: credentials.username || credentials.email,
          password: credentials.password
        })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.detail || 'Login failed')
      }

      const data = await response.json()
      
      // Save auth data
      setToken(data.access_token)
      setUser(data.user)
      setIsAuthenticated(true)
      
      localStorage.setItem('token', data.access_token)
      localStorage.setItem('user', JSON.stringify(data.user))
      
      toast.success('Login successful!')
      return data
    } catch (error) {
      console.error('Login error:', error)
      toast.error(error.message || 'Login failed')
      throw error
    } finally {
      setLoading(false)
    }
  }

  const register = async (userData) => {
    try {
      setLoading(true)
      
      const response = await fetch(`${import.meta.env.VITE_API_URL || '  https://api.testcrew.ai'}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.detail || 'Registration failed')
      }

      const data = await response.json()
      toast.success('Registration successful! Please login.')
      return data
    } catch (error) {
      console.error('Registration error:', error)
      toast.error(error.message || 'Registration failed')
      throw error
    } finally {
      setLoading(false)
    }
  }

  const logout = () => {
    setUser(null)
    setToken(null)
    setIsAuthenticated(false)
    
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    
    toast.success('Logged out successfully')
  }

  const updateUser = (updates) => {
    const updatedUser = { ...user, ...updates }
    setUser(updatedUser)
    localStorage.setItem('user', JSON.stringify(updatedUser))
  }

  const changePassword = async (passwordData) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || '  https://api.testcrew.ai'}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(passwordData)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.detail || 'Password change failed')
      }

      toast.success('Password changed successfully!')
      return await response.json()
    } catch (error) {
      console.error('Change password error:', error)
      toast.error(error.message || 'Password change failed')
      throw error
    }
  }

  const value = {
    // State
    user,
    token,
    loading,
    isAuthenticated,
    
    // Actions
    login,
    register,
    logout,
    updateUser,
    changePassword,
    verifyToken
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
export default AuthContext;